// frontend/src/design-system/useApi.ts
import * as React from 'react';
import { api, ApiError } from '../core/api-client';

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** HTTP status of the failed request when known (e.g. 403), else null. */
  status: number | null;
}

export interface UseApiResult<T> extends UseApiState<T> {
  reload: () => void;
}

/**
 * Tiny async-fetch hook with loading/error/data. Re-runs when `deps` change.
 * For mutations, call `api()` directly and then `result.reload()`.
 */
export function useApi<T>(path: string | null, deps: React.DependencyList = []): UseApiResult<T> {
  const [state, setState] = React.useState<UseApiState<T>>({ data: null, loading: path !== null, error: null, status: null });
  const [tick, setTick] = React.useState(0);

  React.useEffect(() => {
    if (path === null) { setState({ data: null, loading: false, error: null, status: null }); return; }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null, status: null }));
    api<T>(path)
      .then((res) => { if (!cancelled) setState({ data: res, loading: false, error: null, status: null }); })
      .catch((err: ApiError | Error) => {
        if (!cancelled) setState({ data: null, loading: false, error: err.message, status: err instanceof ApiError ? err.status : null });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, tick, ...deps]);

  return { ...state, reload: () => setTick((t) => t + 1) };
}
